import { CustomButton } from "@/components/custom/custom-button";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { showToast } from "@/redux/actions/toast-actions";
import { newGlSchema } from "@/schemas/gl.schema";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  styled,
  Stack,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { FormTextArea } from "@/components/custom/custom-form-textarea";
import {
  ACCOUNT_TYPE_OPTIONS,
  BOOLEAN_OPTIONS,
  CURRENCY_OPTIONS,
  GL_CATEGORIES,
  NORMAL_BALANCE_OPTIONS,
} from "../../data";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 500,
    width: "100%",
  },
}));

const GlDialog = ({ open, onClose, mutationFn, gl }) => {
  const isEditing = Boolean(gl);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const formik = useFormik({
    initialValues: {
      accountType: gl?.accountType ?? "",
      category: gl?.category ?? "",
      code: gl?.code ?? "",
      currency: gl?.currency ?? "",
      description: gl?.description ?? "",
      isControlAccount: gl?.isControlAccount ?? "",
      isPostable: gl?.isPostable ?? "",
      name: gl?.name ?? "",
      normalBalance: gl?.normalBalance ?? "",
    },
    enableReinitialize: true,
    validationSchema: newGlSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      mutation.mutate(values);
    },
  });

  const mutation = useMutation({
    mutationFn: (payload) => mutationFn(payload),
    onSuccess: (data) => {
      formik.resetForm();
      dispatch(
        showToast(
          data?.message ??
            (isEditing ? "GL account updated" : "GL account created"),
          "success",
        ),
      );
      queryClient.invalidateQueries({ queryKey: ["gl-accounts"] });
      onClose();
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Something went wrong", "error"));
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    mutation.reset();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 2,
          pt: 2,
          pb: 0,
        }}
      >
        <Box>
          <DialogTitle
            sx={{
              p: 0,
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {isEditing ? "Update GL Account" : "Create GL Account"}
          </DialogTitle>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {isEditing
              ? "Edit the details below to update this GL account."
              : "Fill in the details below to create a new GL account."}
          </Typography>
        </Box>

        <Stack spacing={2}>
          <FormTextInput formik={formik} name="code" label="Account Code" />
          <FormTextInput formik={formik} name="name" label="Account Name" />
          <FormSelectInput
            formik={formik}
            name="category"
            label="Category"
            options={GL_CATEGORIES}
          />
          <FormSelectInput
            formik={formik}
            name="currency"
            label="Currency"
            options={CURRENCY_OPTIONS}
          />

          <FormSelectInput
            formik={formik}
            name="accountType"
            label="Account Type"
            options={ACCOUNT_TYPE_OPTIONS}
          />
          <FormSelectInput
            formik={formik}
            name="normalBalance"
            label="Normal Balance"
            options={NORMAL_BALANCE_OPTIONS}
          />
          <FormSelectInput
            formik={formik}
            name="isControlAccount"
            label="Control Account"
            options={BOOLEAN_OPTIONS}
          />
          <FormSelectInput
            formik={formik}
            name="isPostable"
            label="Postable"
            options={BOOLEAN_OPTIONS}
          />
          <FormTextArea
            formik={formik}
            name="description"
            label="Description"
            rows={3}
          />
        </Stack>

        {mutation.isError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {mutation.error?.data?.[0] ?? "Failed. Try again."}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 2, pb: 1, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
          sx={{ px: 2 }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || !formik.dirty || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending
            ? isEditing
              ? "Updating..."
              : "Creating..."
            : isEditing
              ? "Update GL Account"
              : "Create GL Account"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default GlDialog;
